import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import React from "react";
import * as Yup from "yup";
import Label from "./Label";
import PostScript from "./PostScript";

interface CheckBoxModalProps {
  open: boolean;
  handleClose: () => void;
  setFormValues: any;
  onSave: (values: any) => void;
}

const AdOnCheckBoxModal: React.FC<CheckBoxModalProps> = ({
  open,
  handleClose,
  setFormValues,
  onSave,
}) => {
  const initialValues = {
    type: 3,
    header: "",
    description: "",
    price: "",
    required: false,
  };

  const formValidation = Yup.object().shape({
    header: Yup.string().required("Header is required"),
    price: Yup.number()
      .typeError("Price must be a number")
      .required("Price is required"),
  });

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "#fff",
          p: 3,
          borderRadius: 2,
          boxShadow: 24,
          width: {
            xs: "80%", // small devices
            sm: "50%", // medium devices
            md: "30%", // large devices
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Check Box
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Formik
          initialValues={initialValues}
          validationSchema={formValidation}
          onSubmit={(values) => {
            onSave(values);
            handleClose();
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
          }) => {
            const handleNumericChange = (
              e: React.ChangeEvent<any>,
              fieldName: string
            ) => {
              const { value } = e.target;
              if (/^\d*$/.test(value) || value === "") {
                const numericValue = value === "" ? "" : Number(value);
                setFieldValue(fieldName, numericValue);
                setFormValues((prevValues: any) => ({
                  ...prevValues,
                  [fieldName]: numericValue,
                }));
              }
            };

            return (
              <Form>
                <Box sx={{ marginY: "0px" }}>
                  <Label label="Header" required={true} />
                  <Field
                    as={TextField}
                    name="header"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.header && Boolean(errors.header)}
                    inputProps={{
                      style: { fontSize: "14px", padding: "15px 10px" },
                    }}
                  />
                  {errors.header && touched.header && (
                    <Typography color="red" fontSize="12px">
                      {errors.header}
                    </Typography>
                  )}
                  <PostScript text="Ex: DJ, Photographer..." />
                </Box>

                <Box sx={{ marginY: "0px" }}>
                  <Label label="Description" required={false} />
                  <Field
                    as={TextField}
                    name="description"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.description && Boolean(errors.description)}
                    inputProps={{
                      style: { fontSize: "14px", padding: "15px 10px" },
                    }}
                  />
                  <PostScript text="Ex: Add A DJ to your experience... " />
                </Box>

                <Box sx={{ marginY: "0px" }}>
                  <Label label="Price" required={true} />
                  <Field
                    as={TextField}
                    type="number"
                    name="price"
                    fullWidth
                    onChange={(e: any) => handleNumericChange(e, "price")}
                    onBlur={handleBlur}
                    error={touched.price && Boolean(errors.price)}
                    inputProps={{
                      style: { fontSize: "14px", padding: "15px 10px" },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                  {errors.price && touched.price && (
                    <Typography color="red" fontSize="12px">
                      {errors.price}
                    </Typography>
                  )}
                </Box>

                {/* Checkbox */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    border: "1px solid #8D8D8D",
                    alignItems: "center",
                    marginBottom: "10px",
                    borderRadius: "8px",
                    padding: "5px",
                    marginY: "10px",
                    gap: "5px",
                  }}
                >
                  <Checkbox
                    name="required"
                    checked={values.required}
                    onChange={handleChange}
                  />
                  <Typography
                    sx={{ fontSize: "13px", fontWeight: 400, color: "#000" }}
                  >
                    Check if this field is required for the customer
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    bgcolor: "#328734",
                    fontWeight: 600,
                    p: 1.5,
                    borderRadius: "8px",
                  }}
                >
                  Add Check Box
                </Button>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AdOnCheckBoxModal;
